import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Crown, KeyRound, Loader2, Plus, Users } from "lucide-react";
import { contextApi } from "@/api/context";
import { useAppContext } from "@/app/ContextProvider";
import { PageLoader, ErrorState } from "@/components/StateViews";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export function LeaguesPage() {
  const { leagues, leagueId, setLeagueId, isLoading, error } = useAppContext();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["leagues"] });

  if (isLoading) return <PageLoader />;
  if (error) return <ErrorState error={error} />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <Users className="size-5 text-primary" /> Ligák
        </h1>
        <p className="text-sm text-muted-foreground">Válaszd ki, melyik ligában tippelsz. Ligánként és bajnokságonként külön adhatod le a tippjeid.</p>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">Ligáim</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {leagues.map((l) => {
            const active = l.id === leagueId;
            return (
              <button
                key={l.id}
                onClick={() => setLeagueId(l.id)}
                className={cn(
                  "flex items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                  active ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
                )}
              >
                <div className={cn("grid size-10 shrink-0 place-items-center rounded-lg", active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                  {active ? <Check className="size-5" /> : <Users className="size-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 font-medium">
                    <span className="truncate">{l.name}</span>
                    {l.isOwner && <Crown className="size-3.5 text-warning" aria-label="Tulajdonos" />}
                  </div>
                  <div className="text-xs text-muted-foreground">{l.memberCount} tag · {l.visibility}</div>
                </div>
                {active && <Badge>Aktív</Badge>}
              </button>
            );
          })}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <CreateLeagueCard onDone={refresh} />
        <JoinByCodeCard onDone={refresh} />
        <PublicLeaguesCard onDone={refresh} />
      </div>
    </div>
  );
}

function CreateLeagueCard({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState("Private");
  const m = useMutation({
    mutationFn: () => contextApi.createLeague({ name, visibility }),
    onSuccess: () => { setName(""); onDone(); },
  });
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><Plus className="size-4" /> Új liga</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-2">
        <div className="space-y-1.5">
          <Label htmlFor="ln">Név</Label>
          <Input id="ln" value={name} onChange={(e) => setName(e.target.value)} placeholder="Pl. Céges liga" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lv">Láthatóság</Label>
          <select id="lv" value={visibility} onChange={(e) => setVisibility(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            <option value="Private">Privát (kóddal)</option>
            <option value="Public">Publikus</option>
          </select>
        </div>
        {m.error && <p className="text-sm text-destructive">{(m.error as Error).message}</p>}
        <Button className="w-full" disabled={!name.trim() || m.isPending} onClick={() => m.mutate()}>
          {m.isPending && <Loader2 className="size-4 animate-spin" />} Létrehozás
        </Button>
      </CardContent>
    </Card>
  );
}

function JoinByCodeCard({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState("");
  const m = useMutation({
    mutationFn: () => contextApi.joinByCode({ code }),
    onSuccess: () => { setCode(""); onDone(); },
  });
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="size-4" /> Csatlakozás kóddal</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-2">
        <div className="space-y-1.5">
          <Label htmlFor="jc">Meghívó kód</Label>
          <Input id="jc" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABC123" />
        </div>
        {m.error && <p className="text-sm text-destructive">{(m.error as Error).message}</p>}
        {m.isSuccess && <p className="text-sm text-success">Sikeres csatlakozás!</p>}
        <Button className="w-full" variant="secondary" disabled={!code.trim() || m.isPending} onClick={() => m.mutate()}>
          {m.isPending && <Loader2 className="size-4 animate-spin" />} Csatlakozás
        </Button>
      </CardContent>
    </Card>
  );
}

function PublicLeaguesCard({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["publicLeagues"], queryFn: contextApi.publicLeagues });
  const join = useMutation({
    mutationFn: (id: string) => contextApi.joinPublic(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["publicLeagues"] }); onDone(); },
  });
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><Users className="size-4" /> Publikus ligák</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-2">
        {q.isLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        {q.data && q.data.length === 0 && <p className="text-sm text-muted-foreground">Nincs elérhető publikus liga.</p>}
        <ul className="space-y-1.5">
          {q.data?.map((l) => (
            <li key={l.id} className="flex items-center gap-2 text-sm">
              <span className="min-w-0 flex-1 truncate">{l.name} <span className="text-xs text-muted-foreground">({l.memberCount})</span></span>
              {l.isMember ? (
                <Badge variant="secondary">Tag</Badge>
              ) : (
                <Button size="sm" variant="outline" disabled={join.isPending} onClick={() => join.mutate(l.id)}>Csatlakozás</Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
